import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CalendarIcon } from 'lucide-react';
import { stringToDateArray } from '../lib/string.lib';

interface DateRangePickerProps {
  onDateRangeChange: (dateRange: string, startDate?: Date, endDate?: Date) => void;
  selectedRange: string;
}

const PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 15 days', value: 'last_15_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
];

function toInputValue(d: Date | undefined): string {
  if (!d || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromInputValue(v: string): Date | undefined {
  if (!v) return undefined;
  const d = new Date(v + 'T00:00:00');
  return isNaN(d.getTime()) ? undefined : d;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ onDateRangeChange, selectedRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Restore saved custom dates on mount
  useEffect(() => {
    if (selectedRange === 'custom') {
      const s = localStorage.getItem('cta_date_range_start_date');
      const e = localStorage.getItem('cta_date_range_end_date');
      if (s) setCustomStart(toInputValue(new Date(s)));
      if (e) setCustomEnd(toInputValue(new Date(e)));
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetSelect = (value: string) => {
    const [start, end] = stringToDateArray(value);
    localStorage.setItem('cta_date_range_type_selected', value);
    localStorage.setItem('cta_date_range_start_date', start.toISOString());
    localStorage.setItem('cta_date_range_end_date', end.toISOString());
    onDateRangeChange(value, start, end);
    setShowCustom(false);
    setIsOpen(false);
  };

  const handleApplyCustom = () => {
    const start = fromInputValue(customStart);
    const end = fromInputValue(customEnd);
    if (!start || !end) return;
    // Set end of day for the end date
    end.setHours(23, 59, 59, 999);
    localStorage.setItem('cta_date_range_type_selected', 'custom');
    localStorage.setItem('cta_date_range_start_date', start.toISOString());
    localStorage.setItem('cta_date_range_end_date', end.toISOString());
    onDateRangeChange('custom', start, end);
    setIsOpen(false);
  };

  const handleCancelCustom = () => {
    setShowCustom(false);
    setCustomStart('');
    setCustomEnd('');
  };

  const getButtonLabel = (): string => {
    if (selectedRange === 'custom') {
      const s = localStorage.getItem('cta_date_range_start_date');
      const e = localStorage.getItem('cta_date_range_end_date');
      if (s && e) {
        const startD = new Date(s);
        const endD = new Date(e);
        return `${startD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endD.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
      return 'Custom range';
    }
    return PRESETS.find(p => p.value === selectedRange)?.label ?? 'Select range';
  };

  const canApply = Boolean(customStart && customEnd && customStart <= customEnd);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-colors"
      >
        <CalendarIcon className="h-4 w-4 text-indigo-500 flex-shrink-0" />
        <span className="max-w-[160px] truncate">{getButtonLabel()}</span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-72 bg-white border border-gray-200 shadow-xl z-30 overflow-hidden">
          {/* Presets */}
          <div className="p-3">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
              Quick ranges
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {PRESETS.map(preset => {
                const isActive = selectedRange === preset.value;
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetSelect(preset.value)}
                    className={`text-sm py-2 px-3 text-left font-medium transition-colors ${isActive
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Custom range toggle */}
          <div className="p-3">
            <button
              type="button"
              onClick={() => setShowCustom(prev => !prev)}
              className={`w-full flex items-center justify-between text-sm font-medium px-3 py-2 transition-colors ${showCustom || selectedRange === 'custom'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <span>Custom range</span>
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-150 ${showCustom ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Custom date inputs */}
            {showCustom && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Start date
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      max={customEnd || toInputValue(new Date())}
                      onChange={e => setCustomStart(e.target.value)}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      End date
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart}
                      max={toInputValue(new Date())}
                      onChange={e => setCustomEnd(e.target.value)}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCancelCustom}
                    className="flex-1 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyCustom}
                    disabled={!canApply}
                    className="flex-1 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
